
import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Container,
  Badge,
  UncontrolledTooltip,
  Col,
  Row,
  Button,
  Card,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
} from "reactstrap";
import ReactBsTable from "components/Tables/ReactBsTable";
import DynamicFormModal from "components/Modal/Form";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import SearchableDropdown from "components/DropDown/SearchableDropdown";
import { DATE_RANGE_OPTIONS } from "variables/constants";
import {
  createInsertionOrder,
  getInsertionOrderList,
  deleteInsertionOrder,
} from "actions/insertionOrder";
import NotificationAlert from "react-notification-alert";
import { INSERTION_ORDERS_STATUS_OPTIONS } from "variables/constants";
import { showAlert } from "components/Common/commonFunctions";
import { getPublisherList } from "actions/publisher";
import { download_insertion_order } from "API";
import { insertionOrderFormFields } from "variables/formFields";
import { INSERTION_ORDER_STATUS_LABELS } from "variables/constants";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const sortFieldMap = {
  publisher: "CompanyName",
  name: "Name",
  startDate: "StartDate",
  endDate: "EndDate",
  version: "Version",
};

const InsertionOrders = () => {
  const navigate = useNavigate();

  const [data, setData] = useState({
    data: [],
    totalRecords: 0,
  });
  const notificationAlertRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [showLoader, setShowLoader] = useState(false);
  const [dropDownData, setdropDownData] = useState({
    publisherData: {
      options: [],
      totalRecords: 0,
      searchString: "",
      pageNum: 1,
    },
  });
  const [publisherOptions, setPublisherOptions] = useState([]);

  // Fixed preview states
  const [previewModal, setPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [currentRow, setCurrentRow] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    dataField: "",
    sortField: "",
    sortOrder: "",
  });

  const [formState, setFormState] = useState({
    values: { status: true },
    errors: {},
  });

  const [filters, setFilters] = useState({
    io_name: "",
    company_name: "",
    date_range: "",
    start_date: "",
    end_date: "",
    documentStatus: "",
    filterParams: [],
  });

  const isEditMode = !!selected;

  //=> Notify function is used for show notification on create.
  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  //===> Deal List Api Call
  const getInsertionOrderListData = async () => {
    try {
      setShowLoader(true);
      const res = await getInsertionOrderList(
        pageNum,
        filters.filterParams,
        "",
        sortConfig.sortField,
        sortConfig.sortOrder,
      );
      if (res.code === 200) {
        setShowLoader(false);
        setData({
          data: res?.data.InsertionOrder,
          totalRecords: res?.data.totalRecords,
        });
      } else {
        setShowLoader(false);
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {
      setShowLoader(false);
    }
  };

  useEffect(() => {
    getInsertionOrderListData();
  }, [pageNum, filters.filterParams, sortConfig.sortField, sortConfig.sortOrder]);

  //==> getDropDownData function is used for getPublisherList and patch to publisher dropdown.
  const getDropDownData = async () => {
    try {
      const res = await getPublisherList(0, []);
      const publisherData =
        res?.code === 200
          ? (res?.data?.Publisher || []).map((item) => ({
              label: item?.companyName || "",
              value: item?.ID,
            }))
          : [];
      setdropDownData((prev) => ({
        ...prev,
        publisherData: {
          ...prev.publisherData,
          options: publisherData,
          totalRecords: res?.data?.totalRecords || 0,
        },
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const getPublisherOptions = async (searchString = "") => {
    try {
      const res = await getPublisherList(0, [], searchString);
      const options =
        res?.code === 200
          ? (res?.data?.Publisher || []).map((item) => ({
              label: item?.companyName || "",
              value: item?.ID || "",
            }))
          : [];
      setPublisherOptions(options);
    } catch (error) {
      console.error("Failed to load publisher options", error);
    }
  };

  useEffect(() => {
    getPublisherOptions();
  }, []);

  const handlePublisherSearch = useCallback(({ query }) => {
    getPublisherOptions(query);
  }, []);

  useEffect(() => {
    const { pageNum, totalRecords, searchString } = dropDownData.publisherData;

    const currentLength = dropDownData.publisherData.options?.length || 0;

    if (searchString) {
      getDropDownData();
      return;
    }

    if (pageNum > 1 && currentLength < totalRecords) {
      getDropDownData();
      return;
    }
  }, [
    dropDownData.publisherData.pageNum,
    dropDownData.publisherData.searchString,
  ]);

  //==> FIXED: handleChange function - handles both event objects and direct value calls
  const handleChange = (fieldOrEvent, valueOrField, maybeValue) => {
    // Case 1: Called as handleChange(fieldName, value) from DynamicFormModal
    if (typeof fieldOrEvent === 'string') {
      const fieldName = fieldOrEvent;
      const fieldValue = valueOrField;
      
      setFormState((prev) => {
        const updatedValues = {
          ...prev.values,
          [fieldName]: fieldValue,
        };

        const { startDate, endDate } = updatedValues;

        // If both dates exist and startDate > endDate → clear endDate
        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
          updatedValues.endDate = "";
        }

        return {
          values: updatedValues,
          errors: {
            ...prev.errors,
            [fieldName]: "",
          },
        };
      });
      return;
    }

    // Case 2: Called as handleChange(e) from normal input events
    const event = fieldOrEvent;
    if (event && event.target) {
      const { name, value } = event.target;
      
      setFormState((prev) => {
        const updatedValues = {
          ...prev.values,
          [name]: value,
        };

        const { startDate, endDate } = updatedValues;

        // If both dates exist and startDate > endDate → clear endDate
        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
          updatedValues.endDate = "";
        }

        return {
          values: updatedValues,
          errors: {
            ...prev.errors,
            [name]: "",
          },
        };
      });
      return;
    }

    // Case 3: Fallback - do nothing
    console.warn("handleChange called with unexpected arguments:", fieldOrEvent, valueOrField);
  };

  //==> validateForm function is used for to check the validation of form.
  const validateForm = () => {
    const errors = {};

    // Required field validation
    insertionOrderFormFields(dropDownData.publisherData.options).forEach(
      (f) => {
        if (f.required && !formState.values[f.name]) {
          errors[f.name] = `${f.label} is required`;
        }
      },
    );

    const { startDate, document } = formState.values;

    // Document validation
    if (!document) {
      errors.document = "Document is required.";
    }

    setFormState((prev) => ({ ...prev, errors }));
    return Object.keys(errors).length === 0;
  };

  //=> formatDate function is used for to show selected images in human readable in filters.
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  //==> handleSubmit function is used for to create a IO.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setShowLoader(true);
    try {
      const payload = {
        name: formState.values.ioName,
        publisherID: Number(formState.values.publisher),
        documentStatus: formState?.values.status ? "SIGNED" : "UNSIGNED",
        version: "1",
      };

      // add startDate only if selected
      if (formState.values.startDate) {
        payload.startDate = moment
          .utc(formState.values.startDate, "YYYY-MM-DD")
          .startOf("day")
          .format("YYYY-MM-DD[T]HH:mm:ss[Z]");
      }

      // add endDate only if selected
      if (formState.values.endDate) {
        payload.endDate = moment
          .utc(formState.values.endDate, "YYYY-MM-DD")
          .endOf("day")
          .format("YYYY-MM-DD[T]HH:mm:ss[Z]");
      }

      const formData = new FormData();

      formData.append("ioData", JSON.stringify(payload));

      // IMPORTANT: same key as backend / Postman
      if (Array.isArray(formState.values.document)) {
        formState.values.document.forEach((file) => {
          formData.append("pdf", file);
        });
      } else {
        formData.append("pdf", formState.values.document);
      }
      const res = await createInsertionOrder(formData);

      if (res.code === 200) {
        notify("success", res?.message);
        getInsertionOrderListData();
        setShowLoader(false);
        setModalOpen(false);
        setSelected(null);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
        setShowLoader(false);
      }
    } catch (err) {
      setShowLoader(false);
      notify("danger", "Upload failed");
    }
  };

  // ===> downloadIO function is used for download IO Document.
  const downloadIO = async (row) => {
    try {
      const response = await download_insertion_order(row.ID);

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const filename =
        row.fileName && row.fileName.endsWith(".pdf")
          ? row.fileName
          : `${row.fileName || "insertion-order"}.pdf`;

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      notify("success", "PDF downloaded successfully");
    } catch (err) {
      console.error(err);
      notify("danger", "Failed to download PDF");
    }
  };

  // openPreviewModal function is used for to open document in preview mode.
  const openPreviewModal = (row, e) => {
    e.stopPropagation();
    if (!row?.fileUrl) {
      notify("warning", "No document available for preview");
      return;
    }

    setCurrentRow(row);
    setPreviewUrl(row.fileUrl);
    setPreviewTitle(row.fileName || "Document Preview");
    setPreviewModal(true);
  };

  // Close preview modal
  const closePreviewModal = () => {
    setPreviewModal(false);
    setPreviewUrl("");
    setPreviewTitle("");
    setCurrentRow(null);
  };

  // Download from preview modal
  const downloadFromPreview = () => {
    if (currentRow) {
      downloadIO(currentRow);
    }
  };

  // ===> columns is used for to show the columns on IO List.
  const columns = [
    {
      dataField: "publisher",
      text: "Publisher",
      formatter: (row) => row?.companyName,
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "name",
      text: "IO Name",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "startDate",
      text: "Start Date",
      formatter: (cell) =>
        cell !== "" ? moment(cell).format("DD MMM YYYY") : "-",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "endDate",
      text: "End Date",
      formatter: (cell) =>
        cell !== "" ? moment(cell).format("DD MMM YYYY") : "-",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "version",
      text: "Version Number",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      text: "Document",
      dataField: "document",
      formatter: (_, row) =>
        row?.fileUrl ? (
          <span
            className="text-info pointer"
            onClick={(e) => openPreviewModal(row, e)}
            style={{ cursor: "pointer", textDecoration: "underline" }}
          >
            {row.fileName || "View Document"}
          </span>
        ) : (
          "-"
        ),
    },
    {
      dataField: "documentStatus",
      text: "Status",
      formatter: (cell) => (
        <Badge color={cell === "SIGNED" ? "success" : "warning"}>{cell}</Badge>
      ),
    },
    {
      text: "Actions",
      dataField: "actions",
      formatter: (_, row) => (
        <div className="d-flex align-items-center">
          {/* View Button */}
          <a
            id={`view${row.id}`}
            className="table-action table-action-info mr-2"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(row);
            }}
          >
            <i className="fas fa-eye" />
          </a>
          <UncontrolledTooltip target={`view${row.id}`}>
            View Details
          </UncontrolledTooltip>

          {/* Download Button */}
          <a
            id={`download${row.id}`}
            className="table-action table-action-success"
            onClick={(e) => {
              e.stopPropagation();
              downloadIO(row);
            }}
          >
            <i className="fas fa-download" />
          </a>
          <UncontrolledTooltip target={`download${row.id}`}>
            Download Document
          </UncontrolledTooltip>
        </div>
      ),
    },
  ];

  // ==> buildFilterParams function is used for to make a filterJson array.
  const buildFilterParams = () => {
    const filterParams = [];
    if (filters?.documentStatus !== "") {
      filterParams.push({
        field: "DocumentStatus",
        condition: "eq",
        filterValues: [filters?.documentStatus],
      });
    }
    if (filters?.company_name !== "") {
      filterParams.push({
        field: "PublisherID",
        condition: "in",
        filterValues: [String(filters?.company_name)],
      });
    }

    if (filters?.io_name !== "") {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [filters?.io_name],
      });
    }

    if (filters?.start_date !== "" && filters?.end_date !== "") {
      filterParams.push({
        field: "CreatedAt",
        condition: "btw",
        filterValues: [filters?.start_date, filters?.end_date],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : "";
  };

  // ===> showCustomDateInputs function is used for set start Date and end Date according to the selection of Date Range dropdown values.
  const showCustomDateInputs = (name, value) => {
    const today = moment().format("YYYY-MM-DD");

    if (value === "Custom Range") {
      setFilters((prev) => ({
        ...prev,
        date_range: value,
        start_date: "",
        end_date: "",
      }));
      return;
    }

    let months = 0;
    if (value === "Last 1 Month") months = 1;
    if (value === "Last 3 Month") months = 3;
    if (value === "Last 6 Month") months = 6;
    if (value === "Last 12 Month") months = 12;

    const startDate = moment().subtract(months, "months").format("YYYY-MM-DD");

    setFilters((prev) => ({
      ...prev,
      date_range: value,
      start_date: startDate,
      end_date: today,
    }));
  };

  //===> handleFilterChange function ise used for set filters values in state.
  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      if (key === "start_date") {
        const startDate = value;
        const endDate = prev.end_date;

        if (endDate && new Date(startDate) <= new Date(endDate)) {
          return {
            ...prev,
            start_date: startDate,
          };
        }

        return {
          ...prev,
          start_date: startDate,
          end_date: "",
        };
      }

      return {
        ...prev,
        [key]: value,
      };
    });
  };

  // ==> isAnyFilterApplied function is used for to check is any filter is emptyor not to avoid api call.
  const isAnyFilterApplied = (filters) => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === "filterParams") {
        return Array.isArray(value) && value.length > 0;
      }
      return value !== "" && value !== null && value !== undefined;
    });
  };

  //==> handleSearch function is used for search the filter
  const handleSearch = () => {
    if (!isAnyFilterApplied(filters)) {
      return;
    }
    const newfilterParams = buildFilterParams();
    setFilters({
      ...filters,
      filterParams: newfilterParams,
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleReset = () => {
    if (!isAnyFilterApplied(filters)) {
      return;
    }

    setFilters({
      io_name: "",
      company_name: "",
      date_range: "",
      start_date: "",
      end_date: "",
      documentStatus: "",
      filterParams: [],
    });
  };

  const handleSortChange = (dataField, sortOrder) => {
    const mappedSortField = sortFieldMap[dataField] || dataField;
    setSortConfig({ dataField, sortField: mappedSortField, sortOrder });
    setPageNum(1);
  };

  //===> filterSection is used for to show filters on table.
  const filterSection = () => {
    return (
      <>
        <Row className="align-items-center">
          <Col md={2}>
            <Input
              type="text"
              placeholder="IO Name"
              value={filters.io_name}
              onChange={(e) => handleFilterChange("io_name", e.target.value)}
            />
          </Col>
          <Col md={2}>
            <SearchableDropdown
              name="company_name"
              options={publisherOptions}
              placeholder="Select Publisher"
              value={filters.company_name}
              onChange={(e) =>
                handleFilterChange("company_name", e.target.value)
              }
              onSearch={handlePublisherSearch}
              isSearchable={true}
            />
          </Col>
          <Col md={2}>
            <SearchableDropdown
              options={INSERTION_ORDERS_STATUS_OPTIONS}
              placeholder="Status"
              value={filters.documentStatus}
              onChange={(e) =>
                handleFilterChange("documentStatus", e.target.value)
              }
              isSearchable={false}
            />
          </Col>
        </Row>

        <Row className="mt-2">
          <>
            <Col md={2}>
              <SearchableDropdown
                options={DATE_RANGE_OPTIONS}
                placeholder="Date Range"
                value={filters.date_range}
                onChange={(e) =>
                  showCustomDateInputs("date_range", e.target.value)
                }
                isSearchable={false}
              />
            </Col>
            <Col md={2}>
              <div className="datepicker-wrapper">
                <DatePicker
                  selected={
                    filters.start_date ? new Date(filters.start_date) : null
                  }
                  onChange={(date) =>
                    handleFilterChange(
                      "start_date",
                      date ? formatDate(date) : "",
                    )
                  }
                  dateFormat="dd MMM yyyy"
                  placeholderText="Start Date"
                  className="form-control custom-datepicker"
                  disabled={filters.date_range !== "Custom Range"}
                />
                <i className="ni ni-calendar-grid-58 text-muted calendar-icon" />
              </div>
            </Col>

            <Col md={2}>
              <div className="datepicker-wrapper">
                <DatePicker
                  selected={
                    filters.end_date ? new Date(filters.end_date) : null
                  }
                  onChange={(date) =>
                    handleFilterChange("end_date", date ? formatDate(date) : "")
                  }
                  dateFormat="dd MMM yyyy"
                  placeholderText="End Date"
                  className="form-control custom-datepicker"
                  disabled={
                    filters.date_range !== "Custom Range" || !filters.start_date
                  }
                  minDate={
                    filters.start_date
                      ? new Date(filters.start_date)
                      : undefined
                  }
                />
                <i className="ni ni-calendar-grid-58 text-muted calendar-icon" />
              </div>
            </Col>
          </>
          <Col md={4}>
            <Button
              onClick={handleSearch}
              disabled={!isAnyFilterApplied(filters)}
              className="filter-btn-gradient-primary btn-md responsive"
            >
              Search
            </Button>

            <Button
              className="secondary"
              disabled={!isAnyFilterApplied(filters)}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </>
    );
  };

  const handleRowClick = (row) => {
    navigate(`/admin/insertion-orders/details`, {
      state: row,
    });
  };

  const openModal = () => {
    const { options, pageNum } = dropDownData.publisherData;
    const currentLength = options?.length || 0;
    setSelected(null);
    setFormState({
      values: { status: true },
      errors: {},
    });
    if (pageNum === 1 && currentLength === 0) {
      getDropDownData();
    }
    setModalOpen(true);
  };

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container className="mt--6" fluid>
        <Card>
          <ReactBsTable
            title="Insertion Orders"
            description="Manage insertion orders and DocuSign status"
            data={data?.data}
            columns={columns}
            keyField="ID"
            addbtntext="Add IO"
            enableRowClick={true}
            handleRowClick={handleRowClick}
            onAddBtnClick={openModal}
            renderFilters={filterSection}
            page={pageNum}
            sizePerPage={10}
            totalRecords={data?.totalRecords}
            onSortChange={handleSortChange}
            defaultSortField={sortConfig.dataField}
            defaultSortOrder={sortConfig.sortOrder}
            onPageChange={(p) => setPageNum(p)}
            showLoader={showLoader}
          />
        </Card>
      </Container>

      <DynamicFormModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        title={isEditMode ? "Edit Insertion Order" : "Add Insertion Order"}
        fields={insertionOrderFormFields(dropDownData.publisherData.options)}
        formData={formState.values}
        errors={formState.errors}
        onChange={handleChange}
        onSubmit={handleSubmit}
        showDocumentUpload={true}
        formState={formState}
        setFormState={setFormState}
        isEditMode={isEditMode}
        trueLabel={INSERTION_ORDER_STATUS_LABELS.TRUE}
        falseLabel={INSERTION_ORDER_STATUS_LABELS.FALSE}
        showLoader={showLoader}
      />

      {/* Document Preview Modal */}
      <Modal
        isOpen={previewModal}
        toggle={closePreviewModal}
        className="modal-xl"
        style={{ maxWidth: "90%" }}
      >
        <ModalHeader toggle={closePreviewModal}>
          {previewTitle}
          <Button
            size="sm"
            className="btn-gradient-primary ml-2"
            onClick={downloadFromPreview}
          >
            <i className="fa fa-download mr-1" /> Download
          </Button>
        </ModalHeader>
        <ModalBody style={{ height: "70vh", padding: 0 }}>
          {previewUrl ? (
            <iframe
              src={previewUrl}
              title="Document Preview"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
              onError={(e) => {
                console.error("Iframe loading error:", e);
              }}
            />
          ) : (
            <div className="text-center p-5">
              <p>No document available for preview</p>
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default InsertionOrders;